/**
 * Shared utilities for payment calculation across different registration types
 */

import { getRegions } from './index';
import { isPaymentRegion } from './helpers';

/**
 * Get region information from municipality or rural area data
 * @param {Object} form - Registration form data with municipality or ruralArea
 * @returns {Promise<Object>} Region object with id, key, and name
 */
export const getRegionFromForm = async (form) => {
  let region = null;

  // Get region from municipality or rural area
  if (form.ruralArea) {
    const regionData = await getRegions();
    const regionInfo = regionData.regions.find(r => r.id === form.ruralArea.regionId);
    region = regionInfo || { id: 'rgn_0', key: 'outside_bc', name: 'Outside BC' };
  } else if (form.municipality) {
    const regionData = await getRegions();
    const regionInfo = regionData.regions.find(r => r.id === form.municipality.regionId);
    region = regionInfo || { id: 'rgn_0', key: 'outside_bc', name: 'Outside BC' };
  }

  // Default to Outside BC if no region found
  if (!region) {
    region = {
      id: 'rgn_0',
      key: 'outside_bc',
      name: 'Outside BC'
    };
  }

  return region;
};

/**
 * Check if region is pledge-only
 * @param {Object} region - Region object with id
 * @returns {boolean} True if pledge-only, false if payment enabled
 */
export const isPledgeOnlyRegion = (region) => {
  return region && region.id ? !isPaymentRegion(region.id) : true;
};

/**
 * Get rate from form data (municipality or rural area)
 * @param {Object} form - Registration form data
 * @returns {number} Rate as a percentage
 */
export const getRateFromForm = (form) => {
  let rate = 0;
  
  if (form.ruralArea && form.ruralArea.rural_rate !== undefined) {
    rate = form.ruralArea.rural_rate;
  } else if (form.municipality && form.municipality.mun_rate !== undefined) {
    rate = form.municipality.mun_rate;
  }
  
  return rate;
};

/**
 * Calculate payment for homeowner
 * @param {number} propertyValue - Property value in dollars
 * @param {number} rate - Rate as percentage
 * @param {string} regionId - Region ID (e.g., 'rgn_5')
 * @returns {number} Monthly payment amount
 */
export const calculateHomeownerPayment = (propertyValue, rate, regionId) => {
  const isOutsideBC = regionId === 'rgn_0';
  return isOutsideBC ? 0 : (propertyValue * rate / 100000);
};

/**
 * Calculate payment for renter
 * @param {number} rent - Monthly rent in dollars
 * @param {string} regionId - Region ID (e.g., 'rgn_5')
 * @returns {number} Monthly payment amount (1% of rent)
 */
export const calculateRenterPayment = (rent, regionId) => {
  const isOutsideBC = regionId === 'rgn_0';
  return isOutsideBC ? 0 : (rent / 100);
};

/**
 * Trigger Google Ads conversion event
 * @param {string} pageType - Type of page (homeowner, renter, business, etc.)
 */
export const triggerConversionEvent = (pageType) => {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-17270934560',
      'page_type': pageType
    });
  }
};
